import { lazy, ReactNode } from "react";
import MyAssessmentIcon from "../../assets/SVGIcons/MyAssessmentIcon.web";
import MyCoursesIcon from "../../assets/SVGIcons/MyCoursesIcon.web";
import MyGradebookIcon from "../../assets/SVGIcons/MyGradebookIcon.web";
import MyPathIcon from "../../assets/SVGIcons/MyPathIcon.web";
import ReadIcon from "../../assets/SVGIcons/ReadIcon.web";
import ParentProfile from "../../components/pages/parentWorkSpace/profile/ParentProfile";
import { NavigationGroup } from ".";
import DashboardIcon from "../../assets/SVGIcons/DashboardIcon.web";


type Props = {
  path: string;
  label: string;
  component: ReactNode;
  icon?: ReactNode;
  index?: boolean;
};

// LAZY ROUTES
const AllStudentProgressPerformance = lazy(
  () => import("../../components/pages/parentWorkSpace/dashboard/AllStudentProgressPerformance"),
);
const AllAssignedAssestments = lazy(
  () => import("../../components/pages/parentWorkSpace/dashboard/AllAssignedAssestments"),
);
const ParentStudents = lazy(
  () => import("../../components/pages/parentWorkSpace/students/ParentStudents"),
);
const ParentCourses = lazy(
  () => import("../../components/pages/parentWorkSpace/courses/ParentCourses"),
);
const ParentCoursesCourseTab = lazy(
  () => import("../../components/pages/parentWorkSpace/courses/partials/tabs/CourseTab"),
);
const ParentCoursesParticipantsTab = lazy(
  () => import("../../components/pages/parentWorkSpace/courses/partials/tabs/ParticipantsTab"),
);
const ParentCourseJourney = lazy(
  () => import("../../components/pages/parentWorkSpace/courses/ParentCourseJourney"),
);
const ParentCalendar = lazy(
  () => import("../../components/pages/parentWorkSpace/assessment/ParentCalendar"),
);
const ParentAssessment = lazy(
  () => import("../../components/pages/parentWorkSpace/assessment/ParentAssessment"),
);
const ParentAssessmentAll = lazy(
  () => import("../../components/pages/parentWorkSpace/assessment/partials/tabs/AllTab"),
);
const ParentAssessmentAssigned = lazy(
  () => import("../../components/pages/parentWorkSpace/assessment/partials/tabs/AssignedTab"),
);
const ParentAssessmentGraded = lazy(
  () => import("../../components/pages/parentWorkSpace/assessment/partials/tabs/GradedTab"),
);
const ParentGradeBook = lazy(
  () => import("../../components/pages/parentWorkSpace/gradebook/ParentGradeBook"),
);

const ParentChildGradeBook = lazy(
  () => import("../../components/pages/parentWorkSpace/gradebook/ParentChildGradeBook"),
);
const ParentAttendance = lazy(
  () => import("../../components/pages/parentWorkSpace/attendance/ParentAttendance"),
);
const ParentDashboard = lazy(
  () => import("../../components/pages/parentWorkSpace/@dashboard/ParentDashboard"),
);
// KnowledgeGraphPage import removed per Chronicle boundary (2026-04-15).
// Component preserved at components/pages/studentWorkSpace/knowledgeGraph/
// as Chronicle incubator material.

// Chronicle integration — iframe pages mounted when org.chronicle_enabled.
// See docs/superpowers/specs/2026-04-27-chronicle-integration-design.md.
const ChronicleDigest = lazy(() => import("@components/pages/chronicle/parent/DigestPage"));

// STAGING-ONLY tab reveal (go-live visibility gates). Hides these nav tabs
// from the production sidebar while keeping the routes registered, so a
// direct link/deep link still resolves. VITE_STAGE2_TABS_UNLOCK is set to
// "true" only on the FE Vercel Preview (staging) env — the production build
// (var absent) keeps hidden:true. Mirrors VITE_PT_UNLOCK_TABS in
// parentTeacherRoutes.tsx.
const STAGE2_TABS_UNLOCKED = import.meta.env.VITE_STAGE2_TABS_UNLOCK === "true";

export const parentRoutes: Props[] = [
  {
    path: "profile",
    label: "profile",
    component: <ParentProfile />,
  },
  {
    path: "courses/:course_id/journey/:student_id",
    label: "Course Journey",
    component: <ParentCourseJourney />,
  },
  {
    path: "grade-book/:student_id",
    label: "Child Gradebook",
    component: <ParentChildGradeBook />,
  },
  {
    path: "students/progress&performace",
    label: "students progress & performace",
    component: <AllStudentProgressPerformance />,
  },
  {
    path: "students/assigned-assesment",
    label: "Assigned Assesment",
    component: <AllAssignedAssestments />,
  },
  // "students/:student_id/knowledge-graph" route removed per Chronicle boundary
  // (2026-04-15). Becomes a Chronicle section feature when Chronicle is enabled.
  // Chronicle iframe routes — degrade gracefully when not configured.
  {
    path: "chronicle/digest",
    label: "Family Digest",
    component: <ChronicleDigest />,
  },
];

export const parentNavigations: NavigationGroup[] = [
  {
    title: "Dashboard",
    routes: [
      {
        label: "dashboard",
        component: <ParentDashboard />,
        icon: <DashboardIcon fontSize="small" />,
        index: true,
        path: "dashboard",
      },
    ],
  },
  {
    title: "Workspace",
    routes: [
      {
        path: "students",
        label: "students",
        component: <ParentStudents />,
        icon: <ReadIcon fontSize="medium" />,
      },
      {
        path: "courses",
        label: "courses",
        component: <ParentCourses />,
        icon: <MyCoursesIcon fontSize="medium" />,
        children: [
          {
            index: true,
            component: <ParentCoursesCourseTab />,
          },
          {
            path: "participants",
            component: <ParentCoursesParticipantsTab />,
          },
        ],
      },
      {
        path: "calendar",
        label: "calendar",
        component: <ParentCalendar />,
        icon: <MyPathIcon fontSize="medium" />,
        hidden: !STAGE2_TABS_UNLOCKED,
      },
      {
        path: "assessment",
        label: "assessment",
        component: <ParentAssessment />,
        icon: <MyAssessmentIcon fontSize="medium" />,
        children: [
          {
            index: true,
            component: <ParentAssessmentAll />
          },
          {
            path: 'assigned',
            component: <ParentAssessmentAssigned />
          },
          {
            path: 'graded',
            component: <ParentAssessmentGraded />
          }
        ]
      },
      {
        path: "grade-book",
        label: "grade book",
        component: <ParentGradeBook />,
        icon: <MyGradebookIcon fontSize="medium" />,
        hidden: !STAGE2_TABS_UNLOCKED,
      },
      {
        path: "attendance",
        label: "attendance",
        component: <ParentAttendance />,
        icon: <ReadIcon fontSize="medium" />,
        hidden: !STAGE2_TABS_UNLOCKED,
      },
    ],
  },
];

export default parentNavigations;
